// domain/services/coverageBilling.js
import { COVERAGE_STATUS } from '../constants/coverage';
import { isVerified } from '../model/EligibilityVerification';
import { coverageStatus } from './coverageStatus';

/**
 * Which coverages are billable right now, and what number each one shows.
 *
 * The central rule: **a coverage's POSITION in the list is independent of whether it is COUNTED in
 * the numbering.** Every coverage in the main list holds an order position (`cobRank`, contiguous
 * and draggable, owned by `coveragePriority`). The displayed number is a separate derivation over
 * the billable subset only. Rows that cannot be billed today still hold their position — dragging
 * one shows where it will land once it becomes billable — they simply are not numbered yet.
 *
 * Keeping the two apart is what lets an upcoming plan be ordered before it starts without ever
 * claiming to be the payer billed today.
 *
 * COB rank is not a display device — it is how payers are sequenced in the real world, and the
 * same question the billing engine asks when it resolves who to bill. `isBillableNow` is therefore
 * a rule the backend's coverage selection needs to agree with, the way `effectiveDating` mirrors
 * the projector's own predicate.
 *
 * TODAY THEY DIVERGE. The projector's `coverageResolver` picks the lowest `cobRank` among
 * coverages active as-of the service date. The projector branch (hr-4410) now consults
 * `eligibilityVerified` and holds on it; it must also learn to read `billingIntents`, because once
 * the pause moved off the coverage document a resolver looking for `onHold` finds nothing and
 * bills a coverage the user paused. That is a MERGE DEPENDENCY, not a divergence to live with.
 */

/**
 * Are we billing on this coverage as of the given date?
 *
 * Three conditions, all required: in effect, eligibility verified, and billing not paused. The
 * billing-order number means "we are actively billing on this payer, in this sequence", so a
 * coverage failing any one of them never takes a number.
 *
 * THE PAUSE IS PASSED IN, not read off the coverage. It lives in a separate aggregate — a
 * `BillingIntent` pointing at the coverage — because "we are choosing not to bill this" is a
 * decision, not a fact about the payer relationship. Handing it in explicitly keeps that honest:
 * the rule needs three inputs and says so, exactly as it does with `asOf` rather than reading a
 * clock.
 *
 * This governs the NUMBERING only — it is not a claim-submission gate. That lives at submission,
 * where a scrubber edit tied to the coverage releases itself when verification flips. Blanket
 * refusal to stage claims for unverified coverage would stall weeks of daily ABA sessions into
 * timely-filing limits.
 *
 * @param {import('../model/Coverage.types').Coverage} coverage
 * @param {string} asOf - 'YYYY-MM-DD'
 * @param {boolean} [isBillingPaused] - is there a live BillingIntent against this coverage
 * @returns {boolean}
 */
export function isBillableNow(coverage, asOf, isBillingPaused = false) {
  return coverageStatus(coverage, asOf) === COVERAGE_STATUS.ACTIVE
    && isVerified(coverage.eligibilityVerification)
    && isBillingPaused !== true;
} // isBillableNow()

/**
 * The billing-order number for each coverage that has one, keyed by id.
 *
 * Numbers run 1..n over the billable subset **in the order given**, so callers must pass the list
 * already sorted by `cobRank`. Non-billable rows are skipped rather than consuming a number, which
 * is why the numbering can read 1, 2 across rows that are not adjacent.
 *
 * Every billable coverage is numbered, including a lone one. Suppressing the number for a single
 * payer reads as missing data on a perfectly healthy coverage, and it would make that row flip
 * from placeholder to "1" the moment a second billable coverage appeared — a visible change to a
 * row that did not change. Numbering always means the same thing, and the placeholder then means
 * exactly one thing: we are not billing on this coverage.
 *
 * @param {import('../model/Coverage.types').Coverage[]} coverages - in list order
 * @param {string} asOf - 'YYYY-MM-DD'
 * @param {Set<string>} [pausedCoverageIds] - coverages with a live BillingIntent against them
 * @returns {Map<string, number>} id → 1-based number; absent means "no number"
 */
export function billingOrderNumbers(coverages, asOf, pausedCoverageIds = new Set()) {
  return new Map(
    coverages
      .filter((coverage) => isBillableNow(coverage, asOf, pausedCoverageIds.has(coverage.id)))
      .map((coverage, index) => [coverage.id, index + 1]),
  );
} // billingOrderNumbers()
